/**
 * The "Payment methods" card of a customer's Settings (docs/features/
 * credit-control/06_ui_strategy.md §2, decisions 9 and 10). Credit control
 * owns it.
 *
 * Payment belongs to the billing account, the payer, and is how that payer may
 * pay: card, bank transfer (pro-forma) and credit account.
 * - three checkboxes; card and pro-forma are invoiced when paid, said so beside
 *   them; credit account brings its terms, PO rule and credit limit,
 *   shown only while it is ticked (ticking it prefills the policy's terms and
 *   limit); at least one method stays ticked; what is typed is refused inline;
 * - credit account has four states, owned by the credit account rather than
 *   the payer: off (unticked); setting up (ticked, not saved: "Becomes active
 *   when saved"); active (saved: an "Active" badge and a low-key "Put on
 *   stop…"); on stop (an amber banner above the methods with the reason, the
 *   waiting orders and Lift stop…; the checkbox stays ticked and locked);
 * - a customer on the default (no billing account) sees the same card showing
 *   card only; saving a change creates the billing account.
 * A sub-account has no card: its "Billed to" row names its payer.
 *
 * The card holds no Save of its own. Its edits live in the page's draft (see
 * `useSettingsDrafts`) and are saved, with one reason, from the page's save
 * bar; a refused save's field errors are bound to their inputs here
 * (api-validation.md). Stopping and lifting a stop are deliberate actions with
 * their own dialogs and do not touch edits in progress. Managers and admins
 * edit (the backend says which, `canManage`); everyone else reads. Wording
 * (method labels, terms labels, PO rules, stop reasons) comes from the response.
 */
import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { History, TriangleAlert } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  BillingAccountView,
  CustomerCreditView,
  PoRule,
  TermsBasis,
} from "@shared-core/data/credit-control-types";
import {
  appPathOrNull,
  CREDIT_LIMIT_RULE_SENTENCE,
} from "@shared-core/domain/credit-control-presentation";
import {
  formatDateTimeOrDash,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import { Checkbox } from "../../primitives/checkbox";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import { RadioGroup, RadioGroupItem } from "../../primitives/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import { CreditHistoryDrawer } from "./CreditHistoryDrawer";
import { ExposureStrip } from "./ExposureStrip";
import { PoFormatField } from "./PoFormatField";
import { LiftStopDialog, StopAccountDialog } from "./CreditStopDialogs";
import type { PaymentSaveError } from "./useSaveSettings";
import type { PaymentDraft } from "./useSettingsDrafts";

/** Where each request-body field of a refused save is shown: the input it names. */
type FieldSlot = "methods" | "terms" | "po" | "poFormat" | "limit";

const FIELD_SLOTS: Record<string, FieldSlot> = {
  payment_methods: "methods",
  terms: "terms",
  "terms.days": "terms",
  "terms.basis": "terms",
  po_rule: "po",
  po_format: "poFormat",
  credit_limit_pence: "limit",
};

const SLOT_MESSAGE_IDS: Record<FieldSlot, string> = {
  methods: "payment-methods-error",
  terms: "credit-terms-error",
  po: "credit-po-error",
  poFormat: "credit-po-format-message",
  limit: "credit-limit-error",
};

/** The refused save's field errors by the input they belong to, and the ones that name no input. */
function bindFieldErrors(error: PaymentSaveError | null) {
  const bound: Partial<Record<FieldSlot, string>> = {};
  const unbound: string[] = [];
  for (const fieldError of error?.fieldErrors ?? []) {
    const slot = FIELD_SLOTS[fieldError.field];
    if (slot)
      bound[slot] = [bound[slot], fieldError.message].filter(Boolean).join(" ");
    else unbound.push(fieldError.message);
  }
  return { bound, unbound };
}

export function PaymentSettingsSection({
  view,
  payment,
  saving,
  error,
  onChanged,
  creditAccountInvoicing,
}: {
  view: CustomerCreditView;
  payment: PaymentDraft;
  /** The page's Save is in flight: nothing here edits meanwhile. */
  saving: boolean;
  /** Why the payment part of the last Save was refused, shown here with the fields. */
  error: PaymentSaveError | null;
  /** A stop was put on or lifted: read the credit view again. */
  onChanged: () => Promise<void>;
  /** The sales ledger's "Invoicing on credit account", nested in the credit account block while it is ticked. */
  creditAccountInvoicing?: ReactNode;
}) {
  return (
    <section
      aria-labelledby="customer-payment-heading"
      className="space-y-4 rounded-lg border border-border p-4 text-sm"
    >
      <h2 id="customer-payment-heading" className="text-base font-medium">
        Payment methods
      </h2>
      <PaymentFields
        view={view}
        payment={payment}
        saving={saving}
        error={error}
        onChanged={onChanged}
        creditAccountInvoicing={creditAccountInvoicing}
      />
    </section>
  );
}

/**
 * Putting the credit account on stop, and lifting it: the two dialogs and the
 * call behind each, posted to the account's own links. A hook because the
 * stop action sits on the credit account row and the lift on the banner.
 */
function useStopActions(
  view: CustomerCreditView,
  account: BillingAccountView | null,
  onChanged: () => Promise<void>,
) {
  const adapter = useDataAdapter();
  const [stopping, setStopping] = useState(false);
  const [lifting, setLifting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (action: () => Promise<unknown>, fallback: string) => {
    setSaving(true);
    setError(null);
    try {
      await action();
      setStopping(false);
      setLifting(false);
      await onChanged();
    } catch (err) {
      setError(mutationErrorMessage(err, fallback));
    } finally {
      setSaving(false);
    }
  };

  return {
    openStop: () => {
      setError(null);
      setStopping(true);
    },
    openLift: () => {
      setError(null);
      setLifting(true);
    },
    dialogs: account && (
      <>
        <StopAccountDialog
          open={stopping}
          onOpenChange={setStopping}
          customerName={view.customerName}
          stopReasonOptions={view.stopReasonOptions}
          error={error}
          saving={saving}
          onConfirm={(input) =>
            void run(
              () => adapter.stopBillingAccount(account, input),
              "Could not put the credit account on stop.",
            )
          }
        />
        <LiftStopDialog
          open={lifting}
          onOpenChange={setLifting}
          customerName={view.customerName}
          waitingOrders={view.pendingHoldCount}
          error={error}
          saving={saving}
          onConfirm={(input) =>
            void run(
              () => adapter.liftBillingAccountStop(account, input),
              "Could not lift the stop.",
            )
          }
        />
      </>
    ),
  };
}

/** The credit ticket the waiting orders were raised on, as the holds' own link says. */
function creditTicketPath(view: CustomerCreditView): string | null {
  const url = view.pendingHolds.find((hold) => hold.ticketUrl)?.ticketUrl;
  return appPathOrNull(url ?? null);
}

/**
 * The credit account is on stop: an amber banner at the top of the section
 * with the reason, since when and by whom, the note, what is waiting, and Lift
 * stop… (managers and admins). Card and pro-forma are unaffected.
 */
function StopBanner({
  view,
  account,
  onLift,
}: {
  view: CustomerCreditView;
  account: BillingAccountView;
  onLift: () => void;
}) {
  const stop = account.stop;
  const ticket = creditTicketPath(view);
  return (
    <Alert className="border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
      <TriangleAlert aria-hidden="true" />
      <AlertDescription className="space-y-1 text-current">
        <p className="font-medium">
          {[
            "Credit account on stop",
            stop?.reasonLabel,
            stop?.at ? `since ${formatShortDateOrDash(stop.at)}` : null,
            stop?.byName,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
        {stop?.note && <p>“{stop.note}”</p>}
        <p>
          {view.pendingHoldCount > 0 ? (
            <>
              Orders on credit account wait for a credit decision:{" "}
              {view.pendingHoldCount} waiting
              {ticket && (
                <>
                  {" → "}
                  <Link
                    to={ticket}
                    className="font-medium underline underline-offset-2"
                  >
                    credit ticket
                  </Link>
                </>
              )}
              . Card and pro-forma are unaffected.
            </>
          ) : (
            "New orders on credit account will wait for a credit decision. Card and pro-forma are unaffected."
          )}
        </p>
        {view.canManage && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-1 border-amber-400 bg-transparent text-current hover:bg-amber-100 dark:border-amber-700 dark:hover:bg-amber-900"
            onClick={onLift}
          >
            Lift stop…
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}

/** "Changed by Lisa S. on 12 Oct 2026, 10:02: “Terms agreed at review”", from the newest change. */
function lastChangeLine(view: CustomerCreditView): string {
  const last = view.recentChanges[0];
  if (!last) return "Never changed.";
  const when = formatDateTimeOrDash(last.at);
  if (last.importLabel) {
    return `Changed by import “${last.importLabel}” on ${when}`;
  }
  return `Changed by ${last.byName ?? "a former user"} on ${when}: “${last.reason ?? ""}”`;
}

function FieldMessage({ id, children }: { id: string; children: ReactNode }) {
  return (
    <p id={id} className="basis-full text-xs text-destructive">
      {children}
    </p>
  );
}

function PaymentFields({
  view,
  payment,
  saving,
  error,
  onChanged,
  creditAccountInvoicing,
}: {
  view: CustomerCreditView;
  payment: PaymentDraft;
  saving: boolean;
  error: PaymentSaveError | null;
  onChanged: () => Promise<void>;
  creditAccountInvoicing?: ReactNode;
}) {
  const account = view.billingAccount;
  const [historyOpen, setHistoryOpen] = useState(false);
  const readOnly = !view.canManage;
  const locked = readOnly || saving;
  const { credit, savedCredit, noMethod } = payment;
  const onStop = !!account?.onStop;
  const { openStop, openLift, dialogs } = useStopActions(
    view,
    account,
    onChanged,
  );
  const { bound, unbound } = bindFieldErrors(error);
  const fieldErrorCount = error?.fieldErrors.length ?? 0;
  const methodsMessage = noMethod
    ? "Choose at least one way the customer pays."
    : bound.methods;

  return (
    <div className="space-y-4">
      {account?.onStop && (
        <StopBanner view={view} account={account} onLift={openLift} />
      )}
      {error && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>
            {fieldErrorCount > 0 ? (
              <>
                <p>
                  Payment settings not saved: {fieldErrorCount}{" "}
                  {fieldErrorCount === 1 ? "problem" : "problems"}
                  {unbound.length < fieldErrorCount ? " marked below" : ""}.
                </p>
                {unbound.map((message) => (
                  <p key={message}>{message}</p>
                ))}
              </>
            ) : (
              error.message
            )}
          </AlertDescription>
        </Alert>
      )}

      <div
        role="group"
        aria-labelledby="customer-payment-heading"
        aria-describedby={methodsMessage ? SLOT_MESSAGE_IDS.methods : undefined}
        aria-invalid={methodsMessage ? true : undefined}
        className="space-y-2"
      >
        {view.paymentMethodOptions.map((option) =>
          option.value === "credit_account" ? (
            <div key={option.value} className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <Checkbox
                  id={`payment-method-${option.value}`}
                  checked={credit}
                  // On stop the box stays ticked and locked: the stop has to
                  // be lifted before credit account can be switched off.
                  disabled={locked || onStop}
                  onCheckedChange={(checked) =>
                    payment.toggle(option.value, checked === true)
                  }
                />
                <Label
                  htmlFor={`payment-method-${option.value}`}
                  className="font-normal"
                >
                  {option.label}
                </Label>
                {credit && savedCredit && (
                  <Badge variant={onStop ? "warning" : "neutral"}>
                    {onStop ? "On stop" : "Active"}
                  </Badge>
                )}
                {credit && savedCredit && !onStop && view.canManage && (
                  <Button
                    type="button"
                    size="sm"
                    variant="link"
                    className="ml-auto h-auto px-0 text-muted-foreground"
                    onClick={openStop}
                  >
                    Put on stop…
                  </Button>
                )}
              </div>
              {onStop && (
                <p className="pl-6 text-xs text-muted-foreground">
                  Lift the stop before switching credit account off.
                </p>
              )}
              {credit && (
                <CreditAccountFields
                  view={view}
                  account={account}
                  settingUp={!savedCredit}
                  disabled={locked}
                  payment={payment}
                  serverErrors={bound}
                >
                  {creditAccountInvoicing}
                </CreditAccountFields>
              )}
            </div>
          ) : (
            <div
              key={option.value}
              className="flex flex-wrap items-center gap-2"
            >
              <Checkbox
                id={`payment-method-${option.value}`}
                checked={payment.methods.includes(option.value)}
                disabled={locked}
                onCheckedChange={(checked) =>
                  payment.toggle(option.value, checked === true)
                }
              />
              <Label
                htmlFor={`payment-method-${option.value}`}
                className="font-normal"
              >
                {option.label}
              </Label>
              <span className="text-xs text-muted-foreground">
                Invoiced when paid
              </span>
            </div>
          ),
        )}
        {methodsMessage && (
          <p
            id={SLOT_MESSAGE_IDS.methods}
            role="alert"
            className="text-xs text-destructive"
          >
            {methodsMessage}
          </p>
        )}
      </div>

      {account && savedCredit && !onStop && view.pendingHoldCount > 0 && (
        <WaitingOrders view={view} />
      )}

      {account && (
        <div className="space-y-1 border-t border-border pt-3">
          <p className="text-xs text-muted-foreground">
            {lastChangeLine(view)}
          </p>
          <Button
            type="button"
            size="sm"
            variant="link"
            className="h-auto px-0"
            onClick={() => setHistoryOpen(true)}
          >
            <History aria-hidden="true" />
            View history
          </Button>
        </div>
      )}

      {dialogs}
      {account && (
        <CreditHistoryDrawer
          accountRef={account.ref}
          accountName={account.name}
          open={historyOpen}
          onOpenChange={setHistoryOpen}
        />
      )}
    </div>
  );
}

/** Orders still waiting for a decision on an account no longer on stop: lifting a stop does not release them. */
function WaitingOrders({ view }: { view: CustomerCreditView }) {
  const ticket = creditTicketPath(view);
  const count = view.pendingHoldCount;
  return (
    <p>
      {count} {count === 1 ? "order" : "orders"} waiting for a credit decision
      {ticket && (
        <>
          {" → "}
          <Link to={ticket} className="text-primary hover:underline">
            Open the credit ticket
          </Link>
        </>
      )}
    </p>
  );
}

/**
 * What credit account brings with it, nested under its checkbox: payment
 * terms, the PO rule (when the customer's PO number is needed) with its PO format and a test box, and a credit limit (required, above zero), then the
 * credit position strip once the account is saved. A quiet note says it
 * becomes active only when saved. Children sit below, still inside the credit
 * account.
 */
function CreditAccountFields({
  view,
  account,
  settingUp,
  disabled,
  payment,
  serverErrors,
  children,
}: {
  view: CustomerCreditView;
  account: BillingAccountView | null;
  settingUp: boolean;
  disabled: boolean;
  payment: PaymentDraft;
  /** A refused save's messages for these fields, by field. */
  serverErrors: Partial<Record<FieldSlot, string>>;
  /** Settings that sit with credit account but belong to the ledger. */
  children?: ReactNode;
}) {
  const { daysInvalid, limitInvalid, limitMissing } = payment;
  const termsMessage = daysInvalid
    ? "Payment terms are a whole number of days from 0 to 365."
    : serverErrors.terms;
  const limitMessage =
    limitInvalid || limitMissing
      ? CREDIT_LIMIT_RULE_SENTENCE
      : serverErrors.limit;
  const poMessage = serverErrors.po;
  return (
    <div className="ml-1.5 space-y-3 border-l-2 border-border pl-4">
      {settingUp && (
        <p className="text-xs text-muted-foreground">
          Becomes active when saved
        </p>
      )}
      <fieldset
        className="flex flex-wrap items-center gap-2"
        disabled={disabled}
      >
        <Label htmlFor="credit-terms-days" className="font-medium">
          Payment terms
        </Label>
        <Input
          id="credit-terms-days"
          type="number"
          min={0}
          max={365}
          className="w-20"
          value={payment.termsDays}
          aria-invalid={termsMessage ? true : undefined}
          aria-describedby={termsMessage ? SLOT_MESSAGE_IDS.terms : undefined}
          onChange={(e) => payment.setTermsDays(e.target.value)}
        />
        <span>days</span>
        <Select
          value={payment.termsBasis}
          onValueChange={(v) => payment.setTermsBasis(v as TermsBasis)}
        >
          <SelectTrigger aria-label="Terms basis" className="w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {view.termsBasisOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {termsMessage && (
          <FieldMessage id={SLOT_MESSAGE_IDS.terms}>
            {termsMessage}
          </FieldMessage>
        )}
      </fieldset>

      <fieldset className="space-y-2" disabled={disabled}>
        <legend id="credit-po-rule-label" className="font-medium">
          PO rule
        </legend>
        <RadioGroup
          value={payment.poRule}
          onValueChange={(value) => payment.setPoRule(value as PoRule)}
          aria-labelledby="credit-po-rule-label"
          aria-invalid={poMessage ? true : undefined}
          aria-describedby={poMessage ? SLOT_MESSAGE_IDS.po : undefined}
          disabled={disabled}
          className="flex flex-wrap gap-x-4 gap-y-2"
        >
          {view.poRuleOptions.map((option) => (
            <div key={option.value} className="flex items-center gap-2">
              <RadioGroupItem
                value={option.value}
                id={`credit-po-rule-${option.value}`}
              />
              <Label
                htmlFor={`credit-po-rule-${option.value}`}
                className="font-normal"
              >
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
        {poMessage && (
          <FieldMessage id={SLOT_MESSAGE_IDS.po}>{poMessage}</FieldMessage>
        )}
      </fieldset>

      {/* A PO format only means something where a PO is needed; the draft keeps it while "Not required". */}
      {payment.poRule !== "not_required" && (
        <PoFormatField
          view={view}
          poRule={payment.poRule}
          value={payment.poFormatText}
          onChange={payment.setPoFormatText}
          disabled={disabled}
          saved={
            account?.poFormat
              ? {
                  format: account.poFormat,
                  label: account.poFormatLabel,
                  example: account.poFormatExample,
                }
              : null
          }
          serverMessage={serverErrors.poFormat}
        />
      )}

      <fieldset
        className="flex flex-wrap items-center gap-2"
        disabled={disabled}
      >
        <Label htmlFor="credit-limit" className="font-medium">
          Credit limit
        </Label>
        <span className="text-muted-foreground">£</span>
        <Input
          id="credit-limit"
          inputMode="decimal"
          className="w-32"
          value={payment.limitText}
          aria-invalid={limitMessage ? true : undefined}
          aria-describedby={limitMessage ? SLOT_MESSAGE_IDS.limit : undefined}
          onChange={(e) => payment.setLimitText(e.target.value)}
        />
        <span className="text-xs text-muted-foreground">
          {account?.creditLimitNote ??
            "Orders that would take the account over the credit limit will wait for finance."}
        </span>
        {limitMessage && (
          <FieldMessage id={SLOT_MESSAGE_IDS.limit}>
            {limitMessage}
          </FieldMessage>
        )}
      </fieldset>
      {!settingUp && (
        <ExposureStrip
          exposure={view.exposure}
          unavailableNote={view.exposureUnavailableNote}
          variant="settings"
          agedDebtSearch={account?.ref}
        />
      )}
      {children}
    </div>
  );
}
